/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — notes-builders.ts
   Note section builders that produce HTML for individual notes.
   ═══════════════════════════════════════════════════════════════════════ */

import { state, type AJEEntry, type AJELine } from './state';
import { esc, rows, sum, el, hasPriorData, fmt, cur, extractYear } from './utils';
import { buildComparativeSection } from './statements';
import { buildPPESchedule, buildInventorySchedule, buildDebtSchedule } from './notes-rules';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface NoteSectionResult {
  id: string;
  title: string;
  html: string;
}

export type NoteSectionBuilder = (company: string, period: string, priorPeriod: string) => NoteSectionResult | null;

// ─── Field readers ─────────────────────────────────────────────────────────

function nqVal(id: string): string {
  const fieldEl = el(id) as HTMLInputElement | null;
  return (fieldEl ? fieldEl.value : '').trim();
}

function nqRadio(name: string): string {
  const radioEl = document.querySelector('input[name="' + name + '"]:checked') as HTMLInputElement | null;
  return radioEl ? radioEl.value : 'no';
}

// ─── Default note section order ────────────────────────────────────────────
export const DEFAULT_NOTE_ORDER: string[] = [
  'operations',
  'accounting',
  'taxes',
  'debt',
  'leases',
  'equity',
  'related',
  'concentrations',
  'commitments',
  'aje',
  'subsequent',
  'going-concern',
  'revenue-detail',
  'opex-detail',
  'depreciation-schedule',
];

// ─── Note Section Builders ────────────────────────────────────────────────

function buildNoteOperations(company: string, _period: string): NoteSectionResult | null {
  const bizDesc: string = nqVal('nq-business-desc');
  const stateVal: string = nqVal('nq-state');
  const formed: string = nqVal('nq-formed');
  const entityType: string = nqVal('nq-entity-type');
  if (!bizDesc && !stateVal && !entityType) return null;
  let h: string = '';
  let p: string = '';
  const an: string = /^([aeiou]|S\b|S )/i.test(entityType) ? 'an' : 'a';
  if (entityType && stateVal && formed) {
    p += esc(company) + ' (the "Company") is ' + an + ' ' + esc(entityType) + ' organized under the laws of the State of ' + esc(stateVal) + ', formed on ' + esc(formed) + '. ';
  } else if (entityType && stateVal) {
    p += esc(company) + ' (the "Company") is ' + an + ' ' + esc(entityType) + ' organized under the laws of the State of ' + esc(stateVal) + '. ';
  } else if (entityType) {
    p += esc(company) + ' (the "Company") is ' + an + ' ' + esc(entityType) + '. ';
  } else {
    p += esc(company) + ' (the "Company") ';
  }
  if (bizDesc) p += esc(bizDesc);
  h += '<p>' + p + '</p>';
  return { id: 'operations', title: 'Nature of Operations', html: h };
}

function buildNoteAccounting(_company: string, _period: string): NoteSectionResult | null {
  const basis: string = nqVal('nq-basis');
  const revRec: string = nqVal('nq-revenue-recognition');
  const cashEquiv: string = nqVal('nq-cash-equiv');
  const hasAllowance: boolean = nqRadio('nq-allowance') === 'yes';
  const allowanceText: string = nqVal('nq-allowance-text');
  const inventory: string = nqVal('nq-inventory');
  const depreciation: string = nqVal('nq-depreciation');
  const usefulLives: string = nqVal('nq-useful-lives');
  const hasIntangibles: boolean = nqRadio('nq-intangibles') === 'yes';
  const intangiblesText: string = nqVal('nq-intangibles-text');
  const advertising: string = nqVal('nq-advertising');

  let h: string = '';

  if (basis) {
    const basisLabels: Record<string, string> = {
      'accrual': 'accrual basis of accounting in accordance with accounting principles generally accepted in the United States of America (U.S. GAAP)',
      'cash': 'cash basis of accounting, which is a comprehensive basis of accounting other than U.S. GAAP. Under this basis, revenue is recognized when received and expenses are recognized when paid',
      'modified-cash': 'modified cash basis of accounting, which is a comprehensive basis of accounting other than U.S. GAAP. This basis modifies the cash basis to record certain items on the accrual basis, such as depreciation and accounts payable',
      'tax': 'income tax basis of accounting, which is a comprehensive basis of accounting other than U.S. GAAP. Under this basis, revenues and expenses are recognized in accordance with applicable income tax regulations',
    };
    h += '<p><strong>Basis of Accounting</strong> — The accompanying financial statements have been prepared on the ' + (basisLabels[basis] || esc(basis)) + '.</p>';
  }

  h += '<p><strong>Use of Estimates</strong> — The preparation of financial statements in conformity with ' + (basis === 'accrual' ? 'U.S. GAAP' : 'the basis of accounting described above') + ' requires management to make estimates and assumptions that affect the reported amounts of assets and liabilities and disclosure of contingent assets and liabilities at the date of the financial statements and the reported amounts of revenues and expenses during the reporting period. Significant estimates include, but are not limited to, the allowance for doubtful accounts, useful lives of property and equipment, and the assessment of contingencies. Actual results could differ from those estimates.</p>';

  if (cashEquiv) {
    h += '<p><strong>Cash and Cash Equivalents</strong> — ' + esc(cashEquiv) + '</p>';
  } else {
    h += '<p><strong>Cash and Cash Equivalents</strong> — The Company considers all highly liquid investments with an original maturity of three months or less when purchased to be cash equivalents.</p>';
  }

  if (revRec) {
    h += '<p><strong>Revenue Recognition</strong> — ' + esc(revRec) + '</p>';
  }

  if (hasAllowance && allowanceText) {
    h += '<p><strong>Accounts Receivable</strong> — Accounts receivable are stated at the amount the Company expects to collect. ' + esc(allowanceText) + ' Receivables are written off when deemed uncollectible.</p>';
  } else {
    const hasAR: boolean = rows(state.currentData, 'current-assets').some(function(r) { return /receivable/i.test(r.label); });
    if (hasAR) {
      h += '<p><strong>Accounts Receivable</strong> — Accounts receivable are stated at the amount the Company expects to collect. The Company evaluates the collectability of receivables on a regular basis and writes off amounts deemed uncollectible. Management has determined that an allowance for doubtful accounts is not necessary at the balance sheet date.</p>';
    }
  }

  if (inventory) {
    h += '<p><strong>Inventory</strong> — Inventory is stated at the lower of cost or net realizable value. Cost is determined using the ' + esc(inventory) + ' method. The Company periodically reviews inventory for obsolescence and adjusts carrying values as necessary.</p>';
    const invTable: string = buildInventorySchedule();
    if (invTable) {
      h += '<p>Inventory consisted of the following:</p>' + invTable;
    }
  }

  if (depreciation) {
    const depLabels: Record<string, string> = {
      'straight-line': 'straight-line method',
      'declining balance': 'declining balance method',
      'MACRS': 'Modified Accelerated Cost Recovery System (MACRS) for tax purposes',
    };
    let depText: string = 'Property and equipment are recorded at cost less accumulated depreciation. Depreciation is computed using the ' + (depLabels[depreciation] || esc(depreciation)) + ' over the estimated useful lives of the respective assets.';
    if (usefulLives) depText += ' Estimated useful lives are as follows: ' + esc(usefulLives) + '.';
    depText += ' Expenditures for major improvements and additions are capitalized, while expenditures for maintenance and repairs are charged to expense as incurred. Upon retirement or disposal, the cost and accumulated depreciation are removed from the accounts, and any resulting gain or loss is reflected in the statements of operations.';
    h += '<p><strong>Property and Equipment</strong> — ' + depText + '</p>';
    const ppeTable: string = buildPPESchedule();
    if (ppeTable) {
      h += '<p>Property and equipment consisted of the following:</p>' + ppeTable;
    }
  }

  if (hasIntangibles && intangiblesText) {
    h += '<p><strong>Intangible Assets</strong> — ' + esc(intangiblesText) + ' The Company reviews intangible assets for impairment whenever events or changes in circumstances indicate that the carrying amount may not be recoverable.</p>';
  }

  if (advertising === 'expensed') {
    h += '<p><strong>Advertising Costs</strong> — Advertising costs are expensed as incurred.</p>';
  } else if (advertising === 'capitalized') {
    h += '<p><strong>Advertising Costs</strong> — Certain direct-response advertising costs are capitalized and amortized over the estimated period of benefit. All other advertising costs are expensed as incurred.</p>';
  }

  return { id: 'accounting', title: 'Summary of Significant Accounting Policies', html: h };
}

function buildNoteTaxes(_company: string, _period: string): NoteSectionResult | null {
  const taxStatus: string = nqVal('nq-tax-status');
  const hasTaxUncertain: boolean = nqRadio('nq-tax-uncertain') === 'yes';
  const taxUncertainText: string = nqVal('nq-tax-uncertain-text');
  if (!taxStatus) return null;

  let h: string = '';
  const taxLanguage: Record<string, string> = {
    's-corp': 'The Company has elected to be taxed as an S Corporation under the Internal Revenue Code. Under this election, the Company\'s taxable income or loss is passed through to and reported on the individual income tax returns of the shareholders. Accordingly, no provision for federal income taxes has been recorded in the accompanying financial statements. The Company may be subject to certain state income taxes.',
    'c-corp': 'The Company is subject to federal and state income taxes as a C Corporation. The Company accounts for income taxes under the asset and liability method, whereby deferred tax assets and liabilities are recognized for the future tax consequences attributable to temporary differences between the financial statement carrying amounts of existing assets and liabilities and their respective tax bases. Deferred tax assets and liabilities are measured using enacted tax rates expected to apply to taxable income in the years in which those temporary differences are expected to be recovered or settled.',
    'llc-partner': 'The Company is organized as a limited liability company treated as a partnership for federal income tax purposes. As such, the Company\'s taxable income or loss is passed through to and reported on the individual income tax returns of its members. Accordingly, no provision for federal or state income taxes has been recorded in the accompanying financial statements.',
    'llc-scorp': 'The Company is organized as a limited liability company that has elected to be taxed as an S Corporation under the Internal Revenue Code. Under this election, the Company\'s taxable income or loss is passed through to and reported on the individual income tax returns of its members. Accordingly, no provision for federal income taxes has been recorded in the accompanying financial statements.',
    'sole-prop': 'The Company is a sole proprietorship, and all income and expenses are reported on the owner\'s individual income tax return. Accordingly, no provision for federal or state income taxes has been recorded in the accompanying financial statements.',
    'nonprofit': 'The Company is a nonprofit organization exempt from federal income tax under Section 501(c)(3) of the Internal Revenue Code and applicable state statutes. Accordingly, no provision for income taxes has been recorded in the accompanying financial statements.',
  };
  h += '<p>' + (taxLanguage[taxStatus] || '') + '</p>';

  if (hasTaxUncertain && taxUncertainText) {
    h += '<p>' + esc(taxUncertainText) + '</p>';
  } else {
    h += '<p>The Company evaluates uncertain tax positions, if any, by applying a more-likely-than-not threshold for financial statement recognition. Management has evaluated the Company\'s tax positions and concluded that there are no uncertain tax positions requiring recognition as of the balance sheet date.</p>';
  }
  return { id: 'taxes', title: 'Income Taxes', html: h };
}

function buildNoteDebt(_company: string, _period: string): NoteSectionResult | null {
  const hasDebt: boolean = nqRadio('nq-debt') === 'yes';
  const debtText: string = nqVal('nq-debt-text');
  const locText: string = nqVal('nq-loc-text');
  if (!(hasDebt && (debtText || locText))) return null;

  let h: string = '';
  if (debtText) h += '<p>' + esc(debtText) + '</p>';
  if (locText) h += '<p><strong>Lines of Credit</strong> — ' + esc(locText) + '</p>';
  const debtSchedule: string = buildDebtSchedule();
  if (debtSchedule) {
    h += '<p>Outstanding debt obligations consisted of the following:</p>' + debtSchedule;
  }
  return { id: 'debt', title: 'Debt and Notes Payable', html: h };
}

function buildNoteLeases(_company: string, _period: string): NoteSectionResult | null {
  const hasLeases: boolean = nqRadio('nq-leases') === 'yes';
  const leasesText: string = nqVal('nq-leases-text');
  const leaseShortTerm: boolean = nqRadio('nq-lease-shortterm') === 'yes';
  if (!(hasLeases && leasesText)) return null;

  let h: string = '';
  h += '<p>The Company determines if an arrangement is a lease at inception. ' + esc(leasesText) + '</p>';
  if (leaseShortTerm) {
    h += '<p>The Company has elected the practical expedient to not recognize right-of-use assets and lease liabilities for short-term leases with a term of 12 months or less. Short-term lease expense is recognized on a straight-line basis over the lease term.</p>';
  }
  return { id: 'leases', title: 'Leases', html: h };
}

function buildNoteEquity(_company: string, _period: string): NoteSectionResult | null {
  const entityType: string = nqVal('nq-entity-type');
  const equityText: string = nqVal('nq-equity-text');
  const hasDist: boolean = nqRadio('nq-distributions') === 'yes';
  const distText: string = nqVal('nq-distributions-text');
  if (!(equityText || (hasDist && distText))) return null;

  const eqTitle: string = (entityType && (entityType.indexOf('LLC') >= 0 || entityType.indexOf('Partnership') >= 0)) ? 'Members\' Equity' : 'Stockholders\' Equity';
  let h: string = '';
  if (equityText) h += '<p>' + esc(equityText) + '</p>';
  if (hasDist && distText) h += '<p><strong>Distributions</strong> — ' + esc(distText) + '</p>';
  const eqTable: string = buildComparativeSection('equity');
  if (eqTable) {
    h += '<p>Components of ' + eqTitle.toLowerCase() + ' consisted of the following:</p>' + eqTable;
  }
  return { id: 'equity', title: eqTitle, html: h };
}

function buildNoteRelated(_company: string, _period: string): NoteSectionResult | null {
  const hasRelated: boolean = nqRadio('nq-related') === 'yes';
  const relatedText: string = nqVal('nq-related-text');
  if (!(hasRelated && relatedText)) return null;
  return { id: 'related', title: 'Related Party Transactions', html: '<p>' + esc(relatedText) + '</p>' };
}

function buildNoteConcentrations(_company: string, _period: string): NoteSectionResult | null {
  const hasFDIC: boolean = nqRadio('nq-fdic') === 'yes';
  const hasCustConc: boolean = nqRadio('nq-cust-conc') === 'yes';
  const hasVendorConc: boolean = nqRadio('nq-vendor-conc') === 'yes';
  const custConcText: string = nqVal('nq-cust-conc-text');
  const vendorConcText: string = nqVal('nq-vendor-conc-text');
  if (!(hasFDIC || hasCustConc || hasVendorConc)) return null;

  let h: string = '';
  if (hasFDIC) {
    h += '<p><strong>Cash Deposits</strong> — The Company maintains its cash balances in financial institutions, which at times may exceed federally insured limits of $250,000 per depositor per institution. The Company has not experienced any losses on such accounts and management believes the Company is not exposed to any significant credit risk on its cash balances.</p>';
  }
  if (hasCustConc && custConcText) {
    h += '<p><strong>Revenue Concentration</strong> — ' + esc(custConcText) + '</p>';
  }
  if (hasVendorConc && vendorConcText) {
    h += '<p><strong>Vendor Concentration</strong> — ' + esc(vendorConcText) + '</p>';
  }
  return { id: 'concentrations', title: 'Concentrations of Credit Risk', html: h };
}

function buildNoteCommitments(_company: string, _period: string): NoteSectionResult | null {
  const hasLitigation: boolean = nqRadio('nq-litigation') === 'yes';
  const litigationText: string = nqVal('nq-litigation-text');
  const hasOtherCommit: boolean = nqRadio('nq-other-commit') === 'yes';
  const otherCommitText: string = nqVal('nq-other-commit-text');

  let h: string = '';
  if (hasLitigation && litigationText) {
    h += '<p><strong>Litigation</strong> — ' + esc(litigationText) + '</p>';
  } else {
    h += '<p><strong>Litigation</strong> — In the normal course of business, the Company may be subject to various claims and legal proceedings. Management is not aware of any pending or threatened litigation that would have a material adverse effect on the Company\'s financial position, results of operations, or cash flows as of the date of these financial statements.</p>';
  }
  if (hasOtherCommit && otherCommitText) {
    h += '<p><strong>Other Commitments</strong> — ' + esc(otherCommitText) + '</p>';
  }
  return { id: 'commitments', title: 'Commitments and Contingencies', html: h };
}

function buildNoteAJE(_company: string, _period: string): NoteSectionResult | null {
  if (!(state.ajePosted && state.ajeEntries.length > 0)) return null;

  let h: string = '';
  h += '<p>The following adjusting entries have been recorded to the financial statements:</p>';
  h += '<table class="nt-table" style="margin:8px 0 16px"><thead><tr><th style="text-align:left">Entry</th><th style="text-align:left">Description</th><th style="text-align:left">Account</th><th style="text-align:right">Debit</th><th style="text-align:right">Credit</th></tr></thead><tbody>';
  const c: string = cur();
  state.ajeEntries.forEach((entry: AJEEntry, eIdx: number) => {
    const hasMultiLines: boolean = entry.lines.length > 1;
    entry.lines.forEach((ln: AJELine, lIdx: number) => {
      const d: number = parseFloat(String(ln.debit)) || 0;
      const cr: number = parseFloat(String(ln.credit)) || 0;
      if (d === 0 && cr === 0) return;
      h += '<tr>';
      if (lIdx === 0) {
        h += '<td' + (hasMultiLines ? ' rowspan="' + entry.lines.length + '"' : '') + '>AJE-' + (eIdx + 1) + '</td>';
        h += '<td' + (hasMultiLines ? ' rowspan="' + entry.lines.length + '"' : '') + '>' + esc(entry.description || '—') + '</td>';
      }
      h += '<td' + (cr > 0 && d === 0 ? ' style="padding-left:20px"' : '') + '>' + esc(ln.account || '—') + '</td>';
      h += '<td style="text-align:right">' + (d > 0 ? c + fmt(d) : '') + '</td>';
      h += '<td style="text-align:right">' + (cr > 0 ? c + fmt(cr) : '') + '</td>';
      h += '</tr>';
    });
  });
  h += '</tbody></table>';
  return { id: 'aje', title: 'Adjusting Journal Entries', html: h };
}

function buildNoteSubsequent(_company: string, _period: string): NoteSectionResult | null {
  const subseqDate: string = nqVal('nq-subseq-date');
  const hasSubseq: boolean = nqRadio('nq-subseq') === 'yes';
  const subseqText: string = nqVal('nq-subseq-text');

  let h: string = '';
  const evalDate: string = subseqDate || 'the date these financial statements were available to be issued';
  h += '<p>The Company has evaluated subsequent events through ' + esc(evalDate) + ', the date the financial statements were available to be issued.';
  if (hasSubseq && subseqText) {
    h += ' ' + esc(subseqText);
  } else {
    h += ' No material subsequent events have occurred since the balance sheet date that require recognition or disclosure in the financial statements.';
  }
  h += '</p>';
  return { id: 'subsequent', title: 'Subsequent Events', html: h };
}

function buildNoteGoingConcern(_company: string, _period: string): NoteSectionResult | null {
  const hasGC: boolean = nqRadio('nq-going-concern') === 'yes';
  const gcText: string = nqVal('nq-gc-text');
  if (!(hasGC && gcText)) return null;

  let h: string = '';
  h += '<p>The accompanying financial statements have been prepared assuming that the Company will continue as a going concern. ' + esc(gcText) + '</p>';
  h += '<p>The financial statements do not include any adjustments relating to the recoverability and classification of recorded asset amounts or the amounts and classification of liabilities that might be necessary should the Company be unable to continue as a going concern.</p>';
  return { id: 'going-concern', title: 'Going Concern', html: h };
}

function buildNoteRevenueDetail(_company: string, period: string, priorPeriod: string): NoteSectionResult | null {
  const revItems = rows(state.currentData, 'revenue');
  const priorRevItems = rows(state.priorData, 'revenue');
  if (revItems.length <= 1) return null;

  const multi: boolean = hasPriorData();
  let h: string = '';
  h += '<p>Revenue consisted of the following for the periods presented:</p>';
  h += '<table class="nt-table" style="margin:8px 0 16px"><thead><tr><th style="text-align:left">Revenue Source</th>';
  const c: string = cur();
  if (multi) {
    const curYear: string = extractYear(period) || 'Current';
    const priorYear: string = extractYear(priorPeriod) || 'Prior';
    h += '<th style="text-align:right">' + esc(curYear) + '</th><th style="text-align:right">' + esc(priorYear) + '</th>';
  } else {
    h += '<th style="text-align:right">Amount</th>';
  }
  h += '</tr></thead><tbody>';
  revItems.forEach((item) => {
    const amt = parseFloat(String(item.amount)) || 0;
    const priorItem = priorRevItems.find(p => p.label === item.label);
    const pAmt = priorItem ? (parseFloat(String(priorItem.amount)) || 0) : 0;
    h += '<tr><td>' + esc(item.label) + '</td><td style="text-align:right">' + c + fmt(amt) + '</td>';
    if (multi) h += '<td style="text-align:right">' + (priorItem ? c + fmt(pAmt) : '—') + '</td>';
    h += '</tr>';
  });
  const revTotal = sum(state.currentData, 'revenue');
  const pRevTotal = multi ? sum(state.priorData, 'revenue') : 0;
  h += '<tr class="nt-total"><td>Total Revenue</td><td style="text-align:right">' + c + fmt(revTotal) + '</td>';
  if (multi) h += '<td style="text-align:right">' + c + fmt(pRevTotal) + '</td>';
  h += '</tr></tbody></table>';
  return { id: 'revenue-detail', title: 'Revenue Detail', html: h };
}

function buildNoteOpexDetail(_company: string, period: string, priorPeriod: string): NoteSectionResult | null {
  const opexItems = rows(state.currentData, 'opex');
  const priorOpexItems = rows(state.priorData, 'opex');
  if (opexItems.length <= 1) return null;

  const multi: boolean = hasPriorData();
  let h: string = '';
  h += '<p>Operating expenses consisted of the following:</p>';
  h += '<table class="nt-table" style="margin:8px 0 16px"><thead><tr><th style="text-align:left">Expense</th>';
  const c: string = cur();
  if (multi) {
    const curYear: string = extractYear(period) || 'Current';
    const priorYear: string = extractYear(priorPeriod) || 'Prior';
    h += '<th style="text-align:right">' + esc(curYear) + '</th><th style="text-align:right">' + esc(priorYear) + '</th>';
  } else {
    h += '<th style="text-align:right">Amount</th>';
  }
  h += '</tr></thead><tbody>';
  opexItems.forEach((item) => {
    const amt = parseFloat(String(item.amount)) || 0;
    const priorItem = priorOpexItems.find(p => p.label === item.label);
    const pAmt = priorItem ? (parseFloat(String(priorItem.amount)) || 0) : 0;
    h += '<tr><td>' + esc(item.label) + '</td><td style="text-align:right">' + c + fmt(amt) + '</td>';
    if (multi) h += '<td style="text-align:right">' + (priorItem ? c + fmt(pAmt) : '—') + '</td>';
    h += '</tr>';
  });
  const opexTotal = sum(state.currentData, 'opex');
  const pOpexTotal = multi ? sum(state.priorData, 'opex') : 0;
  h += '<tr class="nt-total"><td>Total Operating Expenses</td><td style="text-align:right">' + c + fmt(opexTotal) + '</td>';
  if (multi) h += '<td style="text-align:right">' + c + fmt(pOpexTotal) + '</td>';
  h += '</tr></tbody></table>';
  return { id: 'opex-detail', title: 'Operating Expenses Detail', html: h };
}

function buildNoteDepreciationSchedule(_company: string, _period: string): NoteSectionResult | null {
  const ncaItems = rows(state.currentData, 'noncurrent-assets');
  const priorNcaItems = rows(state.priorData, 'noncurrent-assets');
  const depPattern = /depreciation|accum/i;
  const curDepItems = ncaItems.filter(i => depPattern.test(i.label));
  const priorDepItems = priorNcaItems.filter(i => depPattern.test(i.label));
  if (!(curDepItems.length > 0 && priorDepItems.length > 0)) return null;

  let h: string = '';
  h += '<p>Changes in accumulated depreciation during the period:</p>';
  h += '<table class="nt-table" style="margin:8px 0 16px"><thead><tr><th style="text-align:left">Account</th><th style="text-align:right">Beginning</th><th style="text-align:right">Expense</th><th style="text-align:right">Ending</th></tr></thead><tbody>';
  const c: string = cur();
  curDepItems.forEach((item) => {
    const endBal = Math.abs(parseFloat(String(item.amount)) || 0);
    const priorMatch = priorDepItems.find(p => p.label === item.label);
    const begBal = priorMatch ? Math.abs(parseFloat(String(priorMatch.amount)) || 0) : 0;
    const expense = endBal - begBal;
    h += '<tr><td>' + esc(item.label) + '</td>';
    h += '<td style="text-align:right">' + c + fmt(begBal) + '</td>';
    h += '<td style="text-align:right">' + c + fmt(expense) + '</td>';
    h += '<td style="text-align:right">' + c + fmt(endBal) + '</td></tr>';
  });
  h += '</tbody></table>';
  return { id: 'depreciation-schedule', title: 'Depreciation Schedule', html: h };
}

export const NOTE_BUILDERS: Record<string, NoteSectionBuilder> = {
  'operations': buildNoteOperations,
  'accounting': buildNoteAccounting,
  'taxes': buildNoteTaxes,
  'debt': buildNoteDebt,
  'leases': buildNoteLeases,
  'equity': buildNoteEquity,
  'related': buildNoteRelated,
  'concentrations': buildNoteConcentrations,
  'commitments': buildNoteCommitments,
  'aje': buildNoteAJE,
  'subsequent': buildNoteSubsequent,
  'going-concern': buildNoteGoingConcern,
  'revenue-detail': buildNoteRevenueDetail,
  'opex-detail': buildNoteOpexDetail,
  'depreciation-schedule': buildNoteDepreciationSchedule,
};
